import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";

const graduationGallery = [
  {
    src: "/assets/images/secondgrad.jpg",
    title: "Graduation Day Celebration",
    caption: "A memorable day of achievement, pride, and celebration.",
  },
  {
    src: "/assets/images/secondgrad.jpeg",
    title: "Class of Excellence",
    caption: "Graduates stepping into the future with confidence and skill.",
  },
  {
    src: "/assets/images/students.jpg",
    title: "Future Leaders",
    caption: "Young professionals ready to shape the next chapter of progress.",
  },
  {
    src: "/assets/images/scouts.jpg",
    title: "Campus Life and Spirit",
    caption: "The vibrant community that supports excellence at KTTI.",
  },
];

const socialLinks = [
  {
    platform: "Facebook",
    handle: "@karumotti",
    description: "Event updates and community stories",
    href: "https://www.facebook.com/karumotti.ac.ke/",
    icon: "Facebook",
    accent: "from-blue-600 to-blue-500",
    text: "text-blue-600",
  },
  {
    platform: "X",
    handle: "@karumotti",
    description: "Quick announcements and highlights",
    href: "https://x.com/karumotti",
    icon: "Twitter",
    accent: "from-slate-900 to-slate-700",
    text: "text-slate-900",
  },
  {
    platform: "Instagram",
    handle: "@karumotti",
    description: "Photo moments and live memories",
    href: "https://www.instagram.com/karumotti/",
    icon: "Instagram",
    accent: "from-pink-600 via-red-500 to-yellow-500",
    text: "text-pink-600",
  },
  {
    platform: "TikTok",
    handle: "@karumotti",
    description: "Short videos and behind-the-scenes clips",
    href: "https://www.tiktok.com/@karumo.tti.meru2",
    icon: "Music2",
    accent: "from-slate-900 via-blue-700 to-cyan-500",
    text: "text-cyan-700",
  },
];

const bookletUrl = "/assets/files/Karumo%202nd%20Graduation%20Booklet.pdf";

const videoCards = [
  {
    title: "2nd Graduation Documentary",
    description: "Official live documentary from the Karumo 2nd graduation celebrations.",
    href: "https://www.youtube.com/watch?v=7xMxmTyPQRY",
    image: "https://img.youtube.com/vi/7xMxmTyPQRY/hqdefault.jpg",
  },
  {
    title: "Karumo 2nd Graduation Video",
    description: "Official video from the Karumo 2nd graduation celebrations.",
    href: "https://youtu.be/-xFzg5d52qI",
    image: "https://img.youtube.com/vi/-xFzg5d52qI/hqdefault.jpg",
  },
  {
    title: "KARUMO TTI Channel",
    description: "Explore more stories, moments, and updates from the institute.",
    href: "https://www.youtube.com/@karumottti",
    image: "https://img.youtube.com/vi/7xMxmTyPQRY/hqdefault.jpg",
  },
];

const GraduationPage = () => {
  const youtubeLiveUrl = "https://www.youtube.com/watch?v=7xMxmTyPQRY";
  const youtubeEmbedUrl = "https://www.youtube.com/embed/7xMxmTyPQRY";
  const karumoTenVideoUrl = "https://www.youtube.com/embed/-xFzg5d52qI";
  const originalTopEmbedUrl = "https://www.youtube.com/embed/7xMxmTyPQRY";
  const youtubeChannelUrl = "https://www.youtube.com/@karumottti";
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((current) => (current + 1) % graduationGallery.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const showPreviousSlide = () => {
    setActiveSlide((current) =>
      current === 0 ? graduationGallery.length - 1 : current - 1
    );
  };

  const showNextSlide = () => {
    setActiveSlide((current) => (current + 1) % graduationGallery.length);
  };

  return (
    <>
      <Helmet>
        <title>
          Media - KARUMO TECHNICAL TRAINING INSTITUTE
        </title>
        <meta
          name="description"
          content="Watch the KARUMO TECHNICAL TRAINING INSTITUTE graduation livestream and celebrate this year's graduates."
        />
      </Helmet>

      <div className="min-h-screen bg-slate-100 text-slate-900">
        <Header />

        <main className="pt-28 pb-16">
          <section className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mb-8 text-center">
              <span className="inline-flex items-center rounded-full bg-yellow-300 px-4 py-2 text-sm font-bold uppercase tracking-[0.2em] text-slate-900">
                🎓 Graduation
              </span>
              <h1 className="mt-6 text-4xl font-black tracking-tight text-slate-900 md:text-6xl">
                KARUMO TTI Celebrates @10 Years of Excellence
              </h1>
              <p className="mx-auto mt-4 max-w-3xl text-lg text-slate-700 md:text-xl">
                Celebrate with the KARUMO TECHNICAL TRAINING INSTITUTE community
                as we honor our graduates and mark another milestone of
                excellence.
              </p>
            </div>

            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a
                href={bookletUrl}
                target="_blank"
                rel="noreferrer"
                download
                className="inline-flex items-center rounded-full bg-amber-500 px-5 py-3 text-base font-semibold text-slate-900 transition duration-300 hover:bg-amber-600 animate-[pulse_1.8s_ease-in-out_infinite] shadow-lg shadow-amber-300/60"
              >
                Download 2nd Graduation Booklet
              </a>
              <a
                href={youtubeChannelUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded-full bg-blue-700 px-5 py-3 text-base font-semibold text-white transition hover:bg-blue-800"
              >
                Visit our YouTube Channel
              </a>
            </div>

            <div className="mt-6 overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-3 shadow-2xl shadow-slate-200/80 md:p-5">
              <div className="aspect-video w-full overflow-hidden rounded-[1.5rem] bg-slate-900">
                <iframe
                  src={originalTopEmbedUrl}
                  title="Karumo YouTube Livestream"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  className="h-full w-full"
                />
              </div>
            </div>

            <div className="mt-6 overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-3 shadow-2xl shadow-slate-200/80 md:p-5">
              <div className="aspect-video w-full overflow-hidden rounded-[1.5rem] bg-slate-900">
                <iframe
                  src={karumoTenVideoUrl}
                  title="Karumo 2nd Graduation Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  className="h-full w-full"
                />
              </div>
            </div>


            <section className="mt-16">
              <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                  <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-700">
                    Documentary & videos
                  </p>
                  <h2 className="mt-2 text-3xl font-black text-slate-900 md:text-4xl">
                    More moments from the celebration
                  </h2>
                </div>
              </div>

              <div className="grid gap-5 md:grid-cols-3">
                {videoCards.map((video) => (
                  <a
                    key={video.title}
                    href={video.href}
                    target="_blank"
                    rel="noreferrer"
                    className="group overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl"
                  >
                    <div className="relative">
                      <img
                        src={video.image}
                        alt={video.title}
                        className="h-52 w-full object-cover transition duration-300 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/10 to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-blue-700 shadow-lg">
                          <Icon name="Play" size={28} />
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
                        YouTube
                      </p>
                      <h3 className="mt-3 text-xl font-black text-slate-900">
                        {video.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">
                        {video.description}
                      </p>
                    </div>
                  </a>
                ))}
              </div>
            </section>

            <section className="mt-16 rounded-[2rem] bg-white p-5 shadow-xl shadow-slate-200/70 md:p-8">
              <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                  <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-700">
                    Graduation Gallery
                  </p>
                  <h2 className="mt-2 text-3xl font-black text-slate-900 md:text-4xl">
                    Moments of excellence
                  </h2>
                </div>
                <p className="max-w-xl text-slate-600">
                  A glimpse into the spirit, pride, and community that make the KTTI graduation experience unforgettable.
                </p>
              </div>

              <div className="grid gap-6 lg:grid-cols-[1.5fr_0.7fr]">
                <div className="relative overflow-hidden rounded-[1.5rem] border border-slate-200 bg-slate-100">
                  <img
                    src={graduationGallery[activeSlide].src}
                    alt={graduationGallery[activeSlide].title}
                    className="h-[420px] w-full object-cover md:h-[520px]"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/75 via-slate-900/10 to-transparent" />

                  <div className="absolute bottom-0 left-0 right-0 p-5 text-white md:p-7">
                    <p className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-300">
                      {graduationGallery[activeSlide].title}
                    </p>
                    <p className="mt-2 max-w-lg text-sm md:text-base">
                      {graduationGallery[activeSlide].caption}
                    </p>
                  </div>

                  <button
                    type="button"
                    aria-label="Previous image"
                    onClick={showPreviousSlide}
                    className="absolute left-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-xl font-bold text-slate-900 shadow-md transition hover:bg-white"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    aria-label="Next image"
                    onClick={showNextSlide}
                    className="absolute right-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-xl font-bold text-slate-900 shadow-md transition hover:bg-white"
                  >
                    ›
                  </button>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                  {graduationGallery.map((image, index) => (
                    <button
                      key={image.title}
                      type="button"
                      onClick={() => setActiveSlide(index)}
                      className={`group overflow-hidden rounded-2xl border text-left transition ${
                        activeSlide === index
                          ? "border-blue-600 ring-2 ring-blue-200"
                          : "border-slate-200 hover:border-blue-300"
                      }`}
                    >
                      <div className="relative">
                        <img
                          src={image.src}
                          alt={image.title}
                          className="h-28 w-full object-cover transition duration-300 group-hover:scale-105 sm:h-32 lg:h-28"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/55 to-transparent" />
                        <span className="absolute bottom-2 left-2 text-xs font-bold uppercase tracking-[0.2em] text-white">
                          {index + 1}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </section>

            <section className="mt-16 rounded-[2rem] bg-gradient-to-br from-blue-50 via-white to-yellow-50 p-5 shadow-xl shadow-slate-200/70 md:p-8">
              <div className="mb-6 text-center md:text-left">
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-700">
                  Social media
                </p>
                <h2 className="mt-2 text-3xl font-black text-slate-900 md:text-4xl">
                  Follow the celebration online
                </h2>
              </div>

              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                {socialLinks.map((item) => (
                  <a
                    key={item.platform}
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                    className="group rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg"
                  >
                    <div className={`mb-4 inline-flex rounded-2xl bg-gradient-to-r ${item.accent} p-3 text-white`}>
                      <Icon name={item.icon} size={24} />
                    </div>
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <h3 className="text-xl font-bold text-slate-900">{item.platform}</h3>
                      <span className={`text-sm font-semibold ${item.text}`}>{item.handle}</span>
                    </div>
                    <p className="mb-5 text-sm leading-relaxed text-slate-600">
                      {item.description}
                    </p>
                    <span className="inline-flex items-center gap-2 text-sm font-bold text-blue-700 transition group-hover:text-blue-900">
                      Open feed
                      <span aria-hidden="true">→</span>
                    </span>
                  </a>
                ))}
              </div>
            </section>
          </section>
        </main>

        <footer className="bg-blue-100 py-10 text-slate-900">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="text-center">
              <p className="text-lg font-semibold">
                KARUMO TECHNICAL TRAINING INSTITUTE
              </p>
              <p className="mt-2 text-slate-700">
                Celebrating excellence, skill, and innovation.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default GraduationPage;
